// ============================================================================
// Beacon — the team layer. The owner invites contractors / inspectors /
// estimators by link; each gets a role-limited seat, is assigned to specific
// deals, and submits costs in the owner's format. Same dual-mode repo pattern
// as scenarios/buy-boxes: cloud `team_members` with a graceful local fallback.
//
// Security note: teammate isolation is enforced in the UI today; DB-level RLS
// scoping (so a teammate cannot read unassigned deals via the API) is a deploy-
// time hardening step once the shared Supabase project is live.
// ============================================================================

import type { Deal } from "../types";
import { getSupabase, isCloud } from "./supabase";

export type TeamRole = "Contractor" | "Inspector" | "Estimator";
export const TEAM_ROLES: TeamRole[] = ["Contractor", "Inspector", "Estimator"];

export interface TeamMember {
  id: string;
  email: string;
  name?: string;
  role: TeamRole;
  inviteCode: string;
  status: "pending" | "active";
  created_at?: string;
}

let counter = 0;
export function newMemberId(): string {
  counter += 1;
  return "tm" + Date.now().toString(36) + counter.toString(36);
}

/** A short, URL-safe invite code. */
export function newInviteCode(): string {
  const rnd = Math.random().toString(36).slice(2, 8);
  return (Date.now().toString(36) + rnd).toUpperCase();
}

/** The link the owner sends a teammate. */
export function inviteLink(code: string): string {
  const base = typeof location !== "undefined" ? location.origin + location.pathname : "https://beacon-iota-seven.vercel.app/";
  return `${base}?invite=${code}`;
}

/** Is this member assigned to this deal? */
export function isAssigned(deal: Deal, memberId: string): boolean {
  return (deal.assignees ?? []).includes(memberId);
}

/** Toggle a member's assignment on a deal, returning the next assignee list. */
export function toggleAssignee(deal: Deal, memberId: string): string[] {
  const cur = deal.assignees ?? [];
  return cur.includes(memberId) ? cur.filter((x) => x !== memberId) : [...cur, memberId];
}

// --- persistence (cloud `team_members` + local fallback) --------------------
const LS = "beacon_team";
function localAll(): TeamMember[] {
  try {
    const raw = localStorage.getItem(LS);
    if (raw) return JSON.parse(raw) as TeamMember[];
  } catch { /* ignore */ }
  return [];
}
function localSave(all: TeamMember[]) {
  try { localStorage.setItem(LS, JSON.stringify(all)); } catch { /* ignore */ }
}

export async function listMembers(): Promise<TeamMember[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("team_members").select("*").order("created_at");
      if (error) throw error;
      if (data) return data as TeamMember[];
    } catch { /* fall through to local */ }
  }
  return localAll();
}

export async function saveMember(m: TeamMember): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("team_members").upsert(m);
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  const all = localAll();
  const i = all.findIndex((x) => x.id === m.id);
  if (i >= 0) all[i] = m; else all.push(m);
  localSave(all);
}

export async function deleteMember(id: string): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("team_members").delete().eq("id", id);
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  localSave(localAll().filter((x) => x.id !== id));
}

export async function findByInviteCode(code: string): Promise<TeamMember | null> {
  const all = await listMembers();
  return all.find((m) => m.inviteCode.toUpperCase() === code.toUpperCase()) ?? null;
}

// --- teammate session (which member is currently acting) --------------------
// Set after a teammate accepts an invite; puts the app into teammate mode.
const TM = "beacon_teammate";
export function getTeammateId(): string | null {
  try { return localStorage.getItem(TM); } catch { return null; }
}
export function setTeammateId(id: string) {
  try { localStorage.setItem(TM, id); } catch { /* ignore */ }
}
export function clearTeammate() {
  try { localStorage.removeItem(TM); } catch { /* ignore */ }
}
